import { makeAutoObservable, action, reaction } from "./mobx-config.js";
import 'virtual-select-plugin/dist/virtual-select.min.js';
import 'virtual-select-plugin/dist/virtual-select.min.css';
import gedcomDataStore from '../../gedcom/stores/gedcomDataStore.js';
import configStore from '../../tabs/fanChart/fanConfigStore.js';
import timelineEventsStore from '../../tabs/timeline/timelineEventsStore.js';
import familyTownsStore from '../../tabs/familyMap/stores/familyTownsStore.js';
import { storeEvents, EVENTS } from './storeEvents.js';
import { logger } from '../../utils/debug/logger.js';

// Phase 6: Import du middleware de décompression
// Phase 12: Import des extracteurs name/surname
// Phase 9.1: Import des calculs birthYear/deathYear
import { getDecompressedEvents, decompressEventWithMetadata, getName, getFirstName, getSurname, getBirthYear, getDeathYear } from '../../gedcom/formatters/individualFormatters.js';

class RootPersonStore {
    root = null;
    rootPersonName = "";
    virtualSelect = null;
    configHistory = [];
    currentConfigIndex = -1;
    downloadManager = null;
    _skipNextDraw = false;
    _isUndoRedoOperation = false;
    _allOptions = []; // Cache des options pour Virtual Select
    _searchDebounceTimer = null;

    // 🚀 Index de recherche pré-calculé pour performance O(1) lookup
    _searchIndex = {
        // Index par préfixe de nom de famille (2 lettres) -> Set d'IDs
        surnameIndex: new Map(),
        // Index par préfixe de prénom (2 lettres) -> Set d'IDs
        nameIndex: new Map(),
        // Données pré-extraites par individu: id -> { surname, name, surnameNorm, nameNorm, nameWords, birthYear, deathYear, birthPlace, shortName, fullName }
        individuals: new Map(),
        // Flag indiquant si l'index est construit
        isBuilt: false
    };

    constructor() {
        makeAutoObservable(this, {
            setRoot: action,
            setRootPersonName: action,
            initializeVirtualSelect: action,
            setVirtualSelectValue: action,
            undo: action,
            redo: action,
            resetHistory: action,
            handleRootChange: action,

            // Non-observables
            virtualSelect: false,
            configHistory: false,
            downloadManager: false,
            _isUndoRedoOperation: false,
            _allOptions: false,
            _searchDebounceTimer: false,
            _searchIndex: false
        });

        reaction(
            () => ({
                root: this.root,
                hasCache: gedcomDataStore.individualsCache.size > 0
            }),
            async ({ root, hasCache }) => {
                if (!root || !hasCache) return;
        
                try {
                    const newHierarchy = this.buildHierarchy(root);
        
                    gedcomDataStore.setHierarchy(newHierarchy);
                    
                    // Obtenir le nom de la personne pour l'inclure dans l'événement
                    const person = gedcomDataStore.individualsCache.get(root);
                    const rootPersonName = person ? (person.name || `${person.firstName || ''} ${person.lastName || ''}`.trim()) : '';
                    
                    // Émettre l'événement avec les données complètes
                    storeEvents.emit(EVENTS.ROOT.CHANGED, { 
                        root, 
                        skipDraw: this._skipNextDraw,
                        rootPersonName: rootPersonName
                    });
                    
                    // Ne PAS mettre à jour l'historique ici car setRoot() le fait déjà
                    // Cela évite la duplication dans l'historique
                    
        
                } catch (error) {
                    logger.ui.error("Error handling root change:", error);
                    logger.ui.groupEnd();
                } finally {
                    this._skipNextDraw = false;
                }
            },
            {
                name: 'RootPersonStore-MainReaction'
            }
        );

        reaction(
            () => this.rootPersonName,
            (newRootPersonName) => {
                if (!newRootPersonName) return;

                // DÉSACTIVÉ: L'initialisation automatique du DownloadManager a été déplacée vers downloadButton.js
                // pour éviter le chargement précoce de pdfmake (3MB) et permettre un vrai lazy loading
                logger.ui.debug("📝 Nom de la personne racine mis à jour:", newRootPersonName);
                
                // runInAction(async () => {
                //     try {
                //         // Import dynamique des fonctionnalités d'exportation
                //         logger.ui.debug("Chargement dynamique des fonctionnalités d'exportation...");
                //         const { updateFilename, DownloadManager } = await import('../downloadManager.js');
                //         logger.ui.debug("✅ Modules d'exportation chargés");
                //         
                //         if (this.downloadManager) {
                //             this.downloadManager.updateRootPersonName(newRootPersonName);
                //         } else {
                //             this.downloadManager = new DownloadManager(newRootPersonName);
                //         }
    
                //         const filename = (__("geneafan.genealogical_fan_of") +
                //             newRootPersonName +
                //             " créé sur genealog.ie"
                //         ).replace(/[|&;$%@"<>()+,]/g, "");
                //         updateFilename(filename);
                //     } catch (error) {
                //         logger.ui.error("❌ Erreur lors du chargement des modules d'exportation:", error);
                //     }
                // });
            },
            {
                name: 'RootPersonStore-DownloadManagerUpdate'
            }
        );

        // Écouter les événements de construction du cache
        storeEvents.subscribe(EVENTS.CACHE.BUILT, () => {
            logger.ui.debug('Cache built, updating root if necessary');
            // Ne pas redéfinir la racine si elle existe déjà
            // Cela évite d'écraser le skipDraw: false défini lors du chargement
        });
    }

    setRoot = action((newRoot, options = {}) => {
        // Réinitialiser _skipNextDraw à false par défaut
        this._skipNextDraw = false;
        
        if (options.skipDraw) {
            this._skipNextDraw = true;
        }
        
        this.root = newRoot;
        
        // Ne mettre à jour l'historique que si ce n'est pas une opération undo/redo
        if (!this._isUndoRedoOperation) {
            this.updateHistory(newRoot);
        }
    
        document.dispatchEvent(new Event('rootChange'));
    });

    setRootPersonName = action((name) => {
        // Ne rien faire si le nom est identique
        if (this.rootPersonName === name) return;
        
        this.rootPersonName = name;
        
        // Émettre un événement ROOT.UPDATED avec le nouveau nom
        if (this.root) {
            storeEvents.emit(EVENTS.ROOT.UPDATED, {
                root: this.root,
                rootPersonName: name
            });
        }
    });

    buildHierarchy(currentRoot) {
        const buildHierarchyStart = performance.now();
        if (!currentRoot) {
            logger.ui.warn("Root is undefined in buildHierarchy");
            return null;
        }

        const config = configStore.getConfig;
        const maxHeight = config.maxGenerations - 1;

        // Vider la timeline dans tous les cas pour forcer le repeuplement avec enrichissement
        logger.ui.debug('🧹 BuildHierarchy: Vidage timeline pour repeuplement avec enrichissement');
        timelineEventsStore.clearEvents();

        const individualsCache = gedcomDataStore.getIndividualsCache();

        // 🌳 Construire l'arbre des descendants en parallèle
        const descendantsStart = performance.now();
        const descendantsTree = this.buildDescendantsTree(currentRoot);
        const descendantsEnd = performance.now();
        logger.ui.perf(`buildDescendantsTree completed in ${Math.round(descendantsEnd - descendantsStart)}ms`);

        const buildRecursive = (
            individualPointer,
            parent,
            sosa,
            height,
            individualsCache,
            config
        ) => {
            if (!individualsCache.has(individualPointer) && individualPointer !== null) {
                return null;
            }

            // Phase 10 Fix: Utiliser getIndividual() pour décompression transparente Phase 10
            const individual = individualPointer ? 
                gedcomDataStore.getIndividual(individualPointer) ||
                this.createFictiveIndividual(individualPointer, sosa, height) :
                this.createFictiveIndividual(individualPointer, sosa, height);

            // Phase 6: Utiliser middleware de décompression pour la timeline
            const decompressedEvents = getDecompressedEvents(individual);
            if (decompressedEvents && decompressedEvents.length > 0) {
                // logger.ui.group(`📅 Collecte des événements pour ${individual.name || 'Individu'} (Sosa: ${sosa})`);
                decompressedEvents.forEach((event) => {
                    // Phase 6 Cloud: Accepter tous les types d'événements enrichis
                    // Inclure les événements familiaux + professionnels + géographiques + administratifs
                    const validTypes = [
                        // Événements familiaux de base
                        'birth', 'death', 'marriage', 'divorce', 'baptism', 'christening', 'adoption',
                        // Événements enrichis - professionnels
                        'occupation', 'promotion', 'retirement', 'business-creation',
                        // Événements enrichis - éducatifs
                        'education', 'diploma', 'graduation', 'certification',
                        // Événements enrichis - géographiques 
                        'residence', 'migration', 'travel',
                        // Événements enrichis - administratifs
                        'immigration', 'naturalization', 'census',
                        // Événements enrichis - religieux étendus
                        'confirmation', 'first-communion', 'bar-mitzvah', 'bat-mitzvah', 'adult-christening',
                        // Événements enrichis - autres
                        'burial', 'cremation', 'emigration', 'probate', 'will'
                    ];
                    if (validTypes.includes(event.type)) {
                        // Enrichir l'événement avec le nom de ville complet si disponible
                        let enrichedEvent = { ...event };
                        
                        // Si on a une townKey, essayer de récupérer le nom complet
                        if (event.townKey && !event.townDisplay) {
                            // L'enrichissement se fera dans timelineEventsStore.addEvent
                            // pour éviter les dépendances circulaires
                        }
                        
                        // logger.ui.debug(`🔄 RootPersonStore: Envoi événement timeline - type: ${event.type}, townKey: ${event.townKey}, town: ${event.town}, date: ${event.date}`);
                        
                        timelineEventsStore.addEvent({
                            ...enrichedEvent,
                            id: individualPointer,
                            personId: individualPointer, // Ajouter personId pour les filtres
                            sosa,
                            // Phase 5 Fix: Ajouter le nom manquant (supprimé de event pour optimiser cache)
                            // Phase 12: Utiliser les extracteurs name/surname (first name only for timeline)
                            name: `${getFirstName(individual) || ''} ${getSurname(individual) || ''}`.trim() || 'Individu inconnu'
                        });
                    } else {
                        // logger.ui.debug(`⚠️ Type d'événement ignoré:`, event.type);
                    }
                });
                // logger.ui.groupEnd();
            }

            // Marriage events are stored in familiesCache (GEDCOM family-level records),
            // not in individual.e. Fetch them via family pointers (individual.fa).
            const familyIds = individual.fa || [];
            if (familyIds.length > 0) {
                const familiesCache = gedcomDataStore.getFamiliesCache();
                const individualsCache = gedcomDataStore.getIndividualsCache();
                for (const familyId of familyIds) {
                    const family = familiesCache.get(familyId);
                    if (family && family.e && Array.isArray(family.e)) {
                        const familyEvents = family.e
                            .map(decompressEventWithMetadata)
                            .filter(e => e && (e.type === 'marriage' || e.type === 'divorce'));
                        for (const event of familyEvents) {
                            // Dedup: only process marriage from husband side (family.h)
                            // to avoid duplicate when both spouses are in hierarchy
                            if (family.h && family.w && individualPointer !== family.h) continue;

                            // Resolve spouse name: try m.s first, fallback to family.h/family.w
                            let spouseName = '';
                            const spouseId = event.m?.s
                                || (individualPointer === family.h ? family.w : family.h);
                            if (spouseId) {
                                const spouse = individualsCache.get(spouseId);
                                if (spouse) {
                                    spouseName = `${getFirstName(spouse) || ''} ${getSurname(spouse) || ''}`.trim();
                                }
                            }
                            timelineEventsStore.addEvent({
                                ...event,
                                eventId: `${familyId}#${event.type}#${event.date || ''}`,
                                id: individualPointer,
                                personId: individualPointer,
                                sosa,
                                name: `${getFirstName(individual) || ''} ${getSurname(individual) || ''}`.trim() || 'Individu inconnu',
                                spouse: spouseName
                            });
                        }
                    }
                }
            }

            let obj = {
                ...individual,
                i: individualPointer, // Phase 11: Ajouter l'ID pour la hiérarchie (timelineStore en a besoin)
                sosa: sosa,
                generation: height,
                parent: parent,
            };


            if (height < maxHeight) {
                const parents = [];

                const fatherPointer = individual.f;
                const motherPointer = individual.m;
                
                // 🔍 DEBUG: Vérifier ce qui est dans individual
                if (height === 0) {  // Log seulement pour la racine
                    logger.ui.debug('🔍 DEBUG individual structure:', {
                        pointer: individualPointer,
                        hasF: 'f' in individual,
                        hasM: 'm' in individual,
                        fatherPointer,
                        motherPointer,
                        keys: Object.keys(individual)
                    });
                }

                if (fatherPointer) {
                    const fatherObj = individualsCache.get(fatherPointer);
                    if (fatherObj) {
                        parents.push(
                            buildRecursive(
                                fatherPointer,
                                obj,
                                sosa * 2,
                                height + 1,
                                individualsCache,
                                config
                            )
                        );
                    } else {
                        logger.ui.debug(`Father not found in cache: ${fatherPointer}`);
                    }
                } else if (config.showMissing) {
                    parents.push(
                        buildRecursive(
                            null,
                            obj,
                            sosa * 2,
                            height + 1,
                            individualsCache,
                            config
                        )
                    );
                }

                if (motherPointer) {
                    const motherObj = individualsCache.get(motherPointer);
                    if (motherObj) {
                        parents.push(
                            buildRecursive(
                                motherPointer,
                                obj,
                                sosa * 2 + 1,
                                height + 1,
                                individualsCache,
                                config
                            )
                        );
                    } else {
                        logger.ui.debug(`Mother not found in cache: ${motherPointer}`);
                    }
                } else if (config.showMissing) {
                    parents.push(
                        buildRecursive(
                            null,
                            obj,
                            sosa * 2 + 1,
                            height + 1,
                            individualsCache,
                            config
                        )
                    );
                }
                obj.children = parents;
            }

            return obj;
        };

        const hierarchy = buildRecursive(
            currentRoot,
            null,
            1,
            0,
            individualsCache,
            config
        );

        logger.ui.debug("Hierarchy built:", hierarchy);

        const buildHierarchyEnd = performance.now();
        const totalAscendantsTime = Math.round(buildHierarchyEnd - buildHierarchyStart);
        logger.ui.perf(`buildHierarchy (ascendants) completed in ${totalAscendantsTime}ms`);

        // 📊 Log performance totale et recommandation Worker
        const totalTime = totalAscendantsTime + Math.round(descendantsEnd - descendantsStart);
        logger.ui.perf(`Total hierarchy building time: ${totalTime}ms`);
        if (totalTime > 100) {
            logger.ui.warn(`⚠️ Hierarchy building took ${totalTime}ms - consider using Web Workers for optimization`);
        }

        // Stocker l'arbre des descendants dans gedcomDataStore
        gedcomDataStore.setDescendantsTree(descendantsTree);

        return hierarchy;
    }

    /**
     * Construit l'arbre des descendants avec numérotation d'Aboville
     * @param {string} rootId - ID de la personne racine
     * @returns {Object|null} Arbre des descendants avec numérotation d'Aboville
     */
    buildDescendantsTree(rootId) {
        if (!rootId) return null;

        const individualsCache = gedcomDataStore.getIndividualsCache();
        const familiesCache = gedcomDataStore.familiesCache;

        const rootPerson = individualsCache.get(rootId);
        if (!rootPerson) return null;

        const rootNode = this.createDescendantNode(rootPerson, rootId);
        this.buildDescendantsRecursive(rootNode, rootId, individualsCache, familiesCache);
        this.assignAbovilleNumbers(rootNode, "1");

        return rootNode;
    }

    /**
     * Crée un nœud pour un descendant
     * @private
     */
    createDescendantNode(personData, personId) {
        // Phase 9.1: Utiliser les formatters pour calculer birthYear/deathYear à la demande
        const birthYear = getBirthYear(personData);
        const deathYear = getDeathYear(personData);
        const name = getName(personData) || '';
        const surname = getSurname(personData) || '';

        logger.ui.debug(`📅 Creating node for ${name} ${surname}: birthYear=${birthYear}, deathYear=${deathYear}`);

        return {
            id: personId,
            name,
            surname,
            sex: personData.g, // Phase 8: propriété 'g' (gender) dans le cache compressé
            birthYear: birthYear || null,
            deathYear: deathYear || null,
            children: []
        };
    }

    /**
     * Construit récursivement les descendants
     * @private
     */
    buildDescendantsRecursive(node, personId, individualsCache, familiesCache) {
        const person = individualsCache.get(personId);

        logger.ui.debug(`🔍 buildDescendantsRecursive for ${personId}:`, {
            personExists: !!person,
            hasFa: person?.fa ? true : false,
            faArray: person?.fa,
            personKeys: person ? Object.keys(person) : []
        });

        // Phase 8: Utiliser 'fa' (families as parent) dans le cache optimisé
        // 'fa' contient les familles où cette personne est parent
        // 's' contient les conjoints, 'b' contient les frères/sœurs, 'pf' la famille des parents
        if (!person || !person.fa) {
            logger.ui.debug(`⚠️ Stopping recursion for ${personId}: ${!person ? 'person not found' : 'no parent families (fa property)'}`);
            return;
        }

        const allChildren = [];

        // Parcourir toutes les familles où cette personne est parent
        person.fa.forEach((familyId, index) => {
            logger.ui.debug(`  📁 Family ${index}: ${familyId}`);
            const family = familiesCache.get(familyId);

            if (!family) {
                logger.ui.debug(`    ⚠️ Family ${familyId} not found in cache`);
                return;
            }

            if (!family.c) {
                logger.ui.debug(`    ⚠️ Family ${familyId} has no children (c property)`);
                return;
            }

            logger.ui.debug(`    ✅ Family ${familyId} has ${family.c.length} children:`, family.c);

            family.c.forEach(childId => {
                const child = individualsCache.get(childId);
                if (!child) {
                    logger.ui.debug(`      ⚠️ Child ${childId} not found in cache`);
                    return;
                }

                // Éviter les doublons (cas de plusieurs unions)
                if (!allChildren.find(c => c.id === childId)) {
                    logger.ui.debug(`      ✅ Adding child ${childId} to descendants`);
                    const childNode = this.createDescendantNode(child, childId);
                    this.buildDescendantsRecursive(childNode, childId, individualsCache, familiesCache);
                    allChildren.push(childNode);
                } else {
                    logger.ui.debug(`      ⏭️ Child ${childId} already added (duplicate)`);
                }
            });
        });

        logger.ui.debug(`✅ Total children found for ${personId}: ${allChildren.length}`);

        // Log des enfants AVANT tri
        logger.ui.debug('📋 Children BEFORE sort:', allChildren.map(c => ({
            name: `${c.name} ${c.surname}`,
            birthYear: c.birthYear
        })));

        // Trier les enfants par année de naissance (ordre chronologique : aîné en premier)
        allChildren.sort((a, b) => {
            const yearA = parseInt(a.birthYear) || 9999; // 9999 pour les dates inconnues (les mettre à la fin)
            const yearB = parseInt(b.birthYear) || 9999;
            // Ordre chronologique croissant : yearA - yearB (année la plus ancienne en premier)
            // Ainsi : 1.1 = aîné (1950), 1.2 = 2ème enfant (1952), 1.3 = cadet (1955)
            const result = yearA - yearB;
            logger.ui.debug(`  Comparing: ${a.name} (${yearA}) vs ${b.name} (${yearB}) => ${result}`);
            return result || a.name.localeCompare(b.name);
        });

        // Log des enfants APRÈS tri
        logger.ui.debug('📋 Children AFTER sort:', allChildren.map(c => ({
            name: `${c.name} ${c.surname}`,
            birthYear: c.birthYear
        })));

        node.children = allChildren;
    }

    /**
     * Assigne les numéros d'Aboville récursivement
     * @private
     */
    assignAbovilleNumbers(node, prefix = "1") {
        node.aboville = prefix;

        if (!node.children || node.children.length === 0) return;

        node.children.forEach((child, index) => {
            this.assignAbovilleNumbers(child, `${prefix}.${index + 1}`);
        });
    }

    createFictiveIndividual(individualPointer, sosa, height) {
        return {
            id: individualPointer,
            name: "",
            surname: "",
            sosa: sosa,
            generation: height,
            gender: sosa % 2 === 0 ? "M" : "F",
            children: [],
            parent: null,
            individualEvents: [],
        };
    }

    /**
     * 🚀 Initialise Virtual Select pour la sélection de la personne racine
     * Optimisé pour les gros fichiers GEDCOM (100K+ individus)
     * Utilise le virtual scroll pour ne rendre que les éléments visibles
     */
    initializeVirtualSelect() {
        const self = this;
        const selectElement = document.querySelector('#individual-select');

        if (!selectElement) {
            logger.ui.error('❌ Element #individual-select non trouvé');
            return;
        }

        // Détruire l'instance existante si présente
        // L'élément DOM a une propriété virtualSelect qui contient l'instance
        if (this.virtualSelect && this.virtualSelect.virtualSelect) {
            this.virtualSelect.virtualSelect.destroy();
            this.virtualSelect = null;
        }

        // Initialiser Virtual Select avec recherche serveur-side simulée
        VirtualSelect.init({
            ele: '#individual-select',
            options: [],
            search: true,
            placeholder: __("geneafan.choose_root_placeholder"),
            noOptionsText: 'Tapez au moins 2 caractères pour rechercher...',
            noSearchResultsText: 'Aucun résultat trouvé',
            searchPlaceholderText: 'Rechercher par nom ou prénom...',
            clearButtonText: 'Effacer',
            optionsCount: 8, // Nombre d'options visibles
            multiple: false,
            showValueAsTags: false,
            allowNewOption: false,
            markSearchResults: true,
            hasOptionDescription: true,
            disableSelectAll: true,
            hideClearButton: false,
            autoSelectFirstOption: false,
            keepAlwaysOpen: false,
            maxWidth: '100%',
            dropboxWidth: '100%',
            zIndex: 9999,
            position: 'bottom',
            textDirection: 'ltr',
            selectedValue: '',
            silentInitialValueSet: true,
            dropboxWrapper: 'body',

            // 🚀 Recherche personnalisée avec debounce
            onServerSearch: (searchValue, virtualSelect) => {
                // Annuler le timer précédent
                if (self._searchDebounceTimer) {
                    clearTimeout(self._searchDebounceTimer);
                }

                // Debounce de 300ms
                self._searchDebounceTimer = setTimeout(() => {
                    if (!searchValue || searchValue.length < 2) {
                        virtualSelect.setServerOptions([]);
                        return;
                    }

                    const results = self._searchIndividuals(searchValue, 500);

                    // Convertir au format Virtual Select
                    const options = results.map(r => ({
                        value: r.value,
                        label: r.text,
                        description: r.birthPlace || ''
                    }));

                    virtualSelect.setServerOptions(options);
                }, 300);
            }
        });

        // Récupérer l'instance
        this.virtualSelect = document.querySelector('#individual-select');

        // Écouter les changements de sélection
        this.virtualSelect.addEventListener('change', () => {
            const selectedValue = this.virtualSelect.value;
            if (selectedValue) {
                this.setRoot(selectedValue);
            }
        });

        logger.ui.info('✅ Virtual Select initialisé');
    }

    // Alias pour compatibilité avec l'ancien code
    initializeTomSelect() {
        this.initializeVirtualSelect();
    }

    /**
     * 🚀 Construit l'index de recherche pré-calculé pour des lookups O(1)
     * À appeler une seule fois après le chargement du GEDCOM
     */
    buildSearchIndex() {
        const startTime = performance.now();
        const rawCache = gedcomDataStore.individualsCache;

        if (!rawCache || rawCache.size === 0) {
            logger.ui.warn('🔍 buildSearchIndex: Cache vide ou non disponible');
            return;
        }

        // Réinitialiser l'index
        this._searchIndex.surnameIndex.clear();
        this._searchIndex.nameIndex.clear();
        this._searchIndex.individuals.clear();

        const PREFIX_LENGTH = 2; // Longueur du préfixe pour l'index

        for (const [individualId, individual] of rawCache.entries()) {
            if (!individual) continue;

            // Extraction des données (format compressé ou standard)
            let name = '', surname = '';
            if (individual.fn) {
                const parts = individual.fn.split('|');
                surname = parts[0] || '';
                name = parts[1] || '';
            } else if (individual.surname !== undefined) {
                name = individual.name || '';
                surname = individual.surname || '';
            } else if (individual.n) {
                name = individual.n || '';
                surname = individual.s || '';
            } else {
                name = individual.name || individual.firstName || '';
                surname = individual.surname || individual.lastName || '';
            }

            // Extraction année naissance/décès depuis événements compressés
            let birthYear = '', deathYear = '', birthTownKey = '';
            if (individual.e && Array.isArray(individual.e)) {
                const birthEvent = individual.e.find(e => e.t === 'fb' || e.type === 'birth');
                const deathEvent = individual.e.find(e => e.t === 'fd' || e.type === 'death');
                if (birthEvent?.d) {
                    birthYear = typeof birthEvent.d === 'number'
                        ? String(birthEvent.d).substring(0, 4)
                        : (birthEvent.d.includes?.('/') ? birthEvent.d.split('/')[2] : String(birthEvent.d).substring(0, 4));
                    birthTownKey = birthEvent.l || '';
                }
                if (deathEvent?.d) {
                    deathYear = typeof deathEvent.d === 'number'
                        ? String(deathEvent.d).substring(0, 4)
                        : (deathEvent.d.includes?.('/') ? deathEvent.d.split('/')[2] : String(deathEvent.d).substring(0, 4));
                }
            }

            // Lieu de naissance pour affichage
            let birthPlace = '';
            if (birthTownKey && familyTownsStore) {
                const townData = familyTownsStore.getTownsData(birthTownKey);
                birthPlace = townData?.town || birthTownKey;
            }

            // Normaliser nom et prénom pour la recherche (une seule fois!)
            const surnameNorm = surname.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
            const nameNorm = name.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
            const nameWords = nameNorm.split(/\s+/).filter(w => w.length > 0);

            // Construire les labels d'affichage. On découpe sur espace ET
            // virgule : un GEDCOM avec plusieurs prénoms séparés par ", "
            // (« Jacques-Henri, Léon ») laissait sinon une virgule parasite
            // collée au premier prénom (« Jacques-Henri, »).
            const firstName = name.split(/[\s,]+/).filter(Boolean)[0] || name;
            const shortName = `${surname} ${firstName} (${birthYear || '?'}${deathYear ? '-' + deathYear : ''})`;
            const fullName = `${surname} ${name} ${individualId} ${birthYear || "?"}-${deathYear || ""}${birthPlace ? ' • ' + birthPlace : ''}`;

            // Stocker les données pré-calculées
            this._searchIndex.individuals.set(individualId, {
                surname,
                name,
                surnameNorm,
                nameNorm,
                nameWords,
                birthYear,
                deathYear,
                birthPlace,
                shortName,
                fullName,
                // Données pour le tri
                _surname: surname.toLowerCase(),
                _firstName: firstName.toLowerCase(),
                _birthYear: birthYear ? parseInt(birthYear, 10) : 9999
            });

            // Indexer par préfixe de nom de famille
            if (surnameNorm.length >= PREFIX_LENGTH) {
                const prefix = surnameNorm.substring(0, PREFIX_LENGTH);
                if (!this._searchIndex.surnameIndex.has(prefix)) {
                    this._searchIndex.surnameIndex.set(prefix, new Set());
                }
                this._searchIndex.surnameIndex.get(prefix).add(individualId);
            }

            // Indexer par préfixe de chaque mot du prénom
            for (const word of nameWords) {
                if (word.length >= PREFIX_LENGTH) {
                    const prefix = word.substring(0, PREFIX_LENGTH);
                    if (!this._searchIndex.nameIndex.has(prefix)) {
                        this._searchIndex.nameIndex.set(prefix, new Set());
                    }
                    this._searchIndex.nameIndex.get(prefix).add(individualId);
                }
            }
        }

        this._searchIndex.isBuilt = true;
        const duration = performance.now() - startTime;
        logger.ui.info(`✅ Index de recherche construit en ${duration.toFixed(2)}ms pour ${this._searchIndex.individuals.size} individus`);
        logger.ui.debug(`   📊 ${this._searchIndex.surnameIndex.size} préfixes de noms, ${this._searchIndex.nameIndex.size} préfixes de prénoms`);
    }

    /**
     * 🚀 Recherche optimisée utilisant l'index pré-calculé
     * Complexité: O(k) où k = nombre de candidats (vs O(n) avant)
     * @param {string} query - Terme de recherche
     * @param {number} limit - Nombre max de résultats
     * @returns {Array} Options pour Virtual Select
     */
    _searchIndividuals(query, limit = 500) {
        // Si l'index n'est pas construit, le construire maintenant
        if (!this._searchIndex.isBuilt) {
            this.buildSearchIndex();
        }

        if (this._searchIndex.individuals.size === 0) {
            logger.ui.warn('🔍 Recherche: Index vide');
            return [];
        }

        const searchTerms = query.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").split(/\s+/).filter(t => t.length > 0);

        if (searchTerms.length === 0) {
            return [];
        }

        const PREFIX_LENGTH = 2;
        let candidateIds = null;

        // Pour chaque terme de recherche, trouver les candidats via l'index
        for (const term of searchTerms) {
            const termCandidates = new Set();
            const prefix = term.substring(0, Math.min(term.length, PREFIX_LENGTH));

            // Chercher dans l'index des noms de famille
            if (prefix.length >= PREFIX_LENGTH) {
                const surnameMatches = this._searchIndex.surnameIndex.get(prefix);
                if (surnameMatches) {
                    for (const id of surnameMatches) {
                        termCandidates.add(id);
                    }
                }

                // Chercher dans l'index des prénoms
                const nameMatches = this._searchIndex.nameIndex.get(prefix);
                if (nameMatches) {
                    for (const id of nameMatches) {
                        termCandidates.add(id);
                    }
                }
            } else {
                // Terme trop court - parcourir tout l'index (rare)
                for (const [, ids] of this._searchIndex.surnameIndex) {
                    for (const id of ids) termCandidates.add(id);
                }
                for (const [, ids] of this._searchIndex.nameIndex) {
                    for (const id of ids) termCandidates.add(id);
                }
            }

            // Intersection des candidats
            if (candidateIds === null) {
                candidateIds = termCandidates;
            } else {
                candidateIds = new Set([...candidateIds].filter(id => termCandidates.has(id)));
            }

            // Optimisation: si plus de candidats, arrêter
            if (candidateIds.size === 0) {
                return [];
            }
        }

        // Filtrer les candidats avec le matching précis
        const results = [];
        for (const individualId of candidateIds) {
            const data = this._searchIndex.individuals.get(individualId);
            if (!data) continue;

            // Vérifier si TOUS les termes de recherche matchent
            let availableSurname = true;
            const availableNames = [...data.nameWords];

            const matches = searchTerms.every(term => {
                if (availableSurname && data.surnameNorm.startsWith(term)) {
                    availableSurname = false;
                    return true;
                }
                const nameIndex = availableNames.findIndex(word => word.startsWith(term));
                if (nameIndex !== -1) {
                    availableNames.splice(nameIndex, 1);
                    return true;
                }
                return false;
            });

            if (!matches) continue;

            results.push({
                value: individualId,
                text: data.shortName,
                title: data.fullName,
                birthPlace: data.birthPlace,
                _surname: data._surname,
                _firstName: data._firstName,
                _birthYear: data._birthYear
            });
        }

        logger.ui.debug(`🔍 Recherche "${query}": ${candidateIds.size} candidats → ${results.length} résultats`);

        // Trier par nom, prénom, puis année de naissance
        results.sort((a, b) => {
            const surnameCompare = a._surname.localeCompare(b._surname, 'fr');
            if (surnameCompare !== 0) return surnameCompare;
            const firstNameCompare = a._firstName.localeCompare(b._firstName, 'fr');
            if (firstNameCompare !== 0) return firstNameCompare;
            return a._birthYear - b._birthYear;
        });

        // Limiter et nettoyer
        return results.slice(0, limit).map(({ _surname, _firstName, _birthYear, ...rest }) => rest);
    }

    setVirtualSelectValue = action((value, label = '') => {
        if (this.virtualSelect) {
            // Si on a une valeur, on doit d'abord ajouter l'option
            if (value && label) {
                this.virtualSelect.setOptions([{ value, label, description: '' }], true);
            }
            this.virtualSelect.setValue(value);
        } else {
            logger.ui.error("Virtual Select instance is not available.");
        }
    });

    // Alias pour compatibilité avec l'ancien code
    setTomSelectValue = action((value) => {
        this.setVirtualSelectValue(value);
    });

    formatName(rootPersonName) {
        if (!rootPersonName) return "";
        let firstName = rootPersonName?.name?.split(" ")[0] || "";
        let surname = rootPersonName?.surname || "";
        return `${firstName} ${surname}`.trim();
    }

    updateHistory = action((newRoot) => {
        // Vérifier si c'est vraiment un changement (pas la même valeur que l'entrée actuelle)
        const currentEntry = this.configHistory[this.currentConfigIndex];
        if (currentEntry && currentEntry.root === newRoot) {
            logger.ui.debug(`🚫 Histoire NON mise à jour: même root=${newRoot}`);
            return; // Pas de changement, ne pas ajouter de doublon
        }
        
        // NOUVEAU: Mode historique de navigation complet
        // On conserve TOUT l'historique, même après un undo
        // Si on n'est pas à la fin, on crée une nouvelle branche dans l'historique
        // mais on conserve l'ancien "futur" accessible
        
        // Vérifier si on est en train de naviguer dans l'historique
        if (this.currentConfigIndex < this.configHistory.length - 1) {
            // On était dans le passé, créer une nouvelle entrée mais conserver le reste
            logger.ui.debug(`🌱 Nouvelle branche d'historique depuis l'index ${this.currentConfigIndex}`);
        }
        
        // Ajouter la nouvelle entrée sans tronquer
        this.configHistory.push({ 
            root: newRoot,
            timestamp: Date.now(),
            fromIndex: this.currentConfigIndex // Pour tracer d'où on vient
        });
        this.currentConfigIndex = this.configHistory.length - 1;
        
        // Limiter la taille de l'historique pour éviter une croissance infinie
        const MAX_HISTORY_SIZE = 100;
        if (this.configHistory.length > MAX_HISTORY_SIZE) {
            this.configHistory.shift(); // Supprimer le plus ancien
            this.currentConfigIndex--;
        }
        
        logger.ui.debug(`📜 Histoire mise à jour: index=${this.currentConfigIndex}, taille=${this.configHistory.length}, root=${newRoot}`);
    });

    undo = action(() => {
        if (this.currentConfigIndex > 0) {
            logger.ui.debug(`🔄 Undo: ${this.currentConfigIndex} → ${this.currentConfigIndex - 1}`, this.configHistory.map(h => h.root));
            
            this.currentConfigIndex--;
            const previousRoot = this.configHistory[this.currentConfigIndex].root;
            
            // Marquer comme opération undo pour éviter la duplication d'historique
            this._isUndoRedoOperation = true;
            this.setRoot(previousRoot, { skipDraw: false }); // Forcer le redessin
            this.setTomSelectValue(previousRoot);
            // Le flag sera remis à false après la réaction dans un timeout
            setTimeout(() => {
                this._isUndoRedoOperation = false;
            }, 100);
        } else {
            logger.ui.debug(`🚫 Undo impossible: currentConfigIndex=${this.currentConfigIndex}`);
        }
    });

    redo = action(() => {
        if (this.currentConfigIndex < this.configHistory.length - 1) {
            logger.ui.debug(`🔄 Redo: ${this.currentConfigIndex} → ${this.currentConfigIndex + 1}`, this.configHistory.map(h => h.root));
            
            this.currentConfigIndex++;
            const nextRoot = this.configHistory[this.currentConfigIndex].root;
            
            // Marquer comme opération redo pour éviter la duplication d'historique
            this._isUndoRedoOperation = true;
            this.setRoot(nextRoot, { skipDraw: false }); // Forcer le redessin
            this.setTomSelectValue(nextRoot);
            // Le flag sera remis à false après la réaction dans un timeout
            setTimeout(() => {
                this._isUndoRedoOperation = false;
            }, 100);
        } else {
            logger.ui.debug(`🚫 Redo impossible: currentConfigIndex=${this.currentConfigIndex}, length=${this.configHistory.length}`);
        }
    });

    resetHistory = action(() => {
        this.configHistory = [];
        this.currentConfigIndex = -1;
        logger.ui.debug(`🗑️ Histoire réinitialisée`);
    });

    // Méthode pour déboguer l'historique
    logHistory() {
        logger.ui.group(`📜 Historique de navigation (${this.configHistory.length} entrées, position: ${this.currentConfigIndex})`);
        this.configHistory.forEach((entry, index) => {
            const marker = index === this.currentConfigIndex ? ' ➤ ACTUEL' : '';
            const person = gedcomDataStore.individualsCache.get(entry.root);
            const name = person ? (person.name || `${person.firstName || ''} ${person.lastName || ''}`.trim()) : 'Inconnu';
            const time = entry.timestamp ? new Date(entry.timestamp).toLocaleTimeString() : '';
            logger.ui.debug(`${index}: ${entry.root} - ${name} ${time}${marker}`);
        });
        logger.ui.debug(`Peut naviguer en arrière: ${this.currentConfigIndex > 0}`);
        logger.ui.debug(`Peut naviguer en avant: ${this.currentConfigIndex < this.configHistory.length - 1}`);
        logger.ui.groupEnd();
    }
    
    // Naviguer à un index spécifique dans l'historique
    goToHistoryIndex = action((index) => {
        if (index >= 0 && index < this.configHistory.length && index !== this.currentConfigIndex) {
            logger.ui.debug(`🗺️ Navigation vers l'index ${index}`);
            this.currentConfigIndex = index;
            const targetRoot = this.configHistory[index].root;
            
            this._isUndoRedoOperation = true;
            this.setRoot(targetRoot, { skipDraw: false });
            this.setTomSelectValue(targetRoot);
            setTimeout(() => {
                this._isUndoRedoOperation = false;
            }, 100);
        }
    })
    
    // Obtenir la liste de l'historique pour l'UI
    getHistoryList() {
        return this.configHistory.map((entry, index) => {
            const person = gedcomDataStore.individualsCache.get(entry.root);
            const name = person ? (person.name || `${person.firstName || ''} ${person.lastName || ''}`.trim()) : 'Inconnu';
            return {
                index,
                root: entry.root,
                name,
                timestamp: entry.timestamp,
                isCurrent: index === this.currentConfigIndex
            };
        });
    }
    
    // Méthode pour nettoyer les doublons consécutifs dans l'historique
    cleanHistory = action(() => {
        const cleaned = [];
        let lastRoot = null;
        
        for (let i = 0; i < this.configHistory.length; i++) {
            const entry = this.configHistory[i];
            if (entry.root !== lastRoot) {
                cleaned.push(entry);
                lastRoot = entry.root;
            }
        }
        
        // Ajuster l'index si nécessaire
        const currentRoot = this.configHistory[this.currentConfigIndex]?.root;
        this.configHistory = cleaned;
        
        // Retrouver le bon index
        this.currentConfigIndex = cleaned.findIndex(entry => entry.root === currentRoot);
        if (this.currentConfigIndex === -1) {
            this.currentConfigIndex = cleaned.length - 1;
        }
        
        logger.ui.debug(`🧹 Histoire nettoyée: ${this.configHistory.length} entrées (index: ${this.currentConfigIndex})`);
    });
}

const rootPersonStore = new RootPersonStore();
export default rootPersonStore;

// Exposer globalement pour debug
if (typeof window !== 'undefined') {
  window.rootPersonStore = rootPersonStore;
  
  // Méthodes utilitaires pour debugging depuis la console
  window.debugHistory = () => {
    rootPersonStore.logHistory();
  };
  
  window.showHistory = () => {
    const history = rootPersonStore.getHistoryList();
    logger.ui.debug('Configuration history:', history);
  };
  
  window.goToHistory = (index) => {
    logger.ui.debug(`🗺️ Navigation vers l'index ${index}`);
    rootPersonStore.goToHistoryIndex(index);
  };
  
  window.cleanHistory = () => {
    logger.ui.debug('🧹 Nettoyage de l\'historique...');
    rootPersonStore.cleanHistory();
    rootPersonStore.logHistory();
  };
  
  window.testUndo = () => {
    logger.ui.debug('🧪 Test Undo (navigation arrière)');
    rootPersonStore.undo();
  };
  
  window.testRedo = () => {
    logger.ui.debug('🧪 Test Redo (navigation avant)');
    rootPersonStore.redo();
  };
}